import { useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';

import { DataTable } from '@/components/composed/DataTable/DataTable';
import { Button } from '@/components/ui/button';
import { canOfferAct } from '@/lib/permissions';
import { useAuthStore } from '@/lib/session/auth-store';
import type { Client, ClientListParams } from '@/lib/api/domains/clients';
import { clientListQuery } from '../api/client-queries';
import { clientColumns } from './client-columns';

export function ClientTable({
  params,
  onPageChange,
  onRowClick,
  onEdit,
}: {
  params: ClientListParams;
  onPageChange: (page: number) => void;
  onRowClick: (client: Client) => void;
  onEdit?: (client: Client) => void;
}) {
  const query = useQuery(clientListQuery(params));
  const session = useAuthStore((s) => s.session);
  // The per-row edit is the F04 entity-edit act (Administrator-only), read
  // per-act from the server-sourced capabilities — a `tpabilling_user` (Viewer)
  // holds the read, not this act, so the control is withheld (E02.F04.R5,
  // AC-9311d02c). Courtesy only; the domain gate refuses regardless.
  const showEdit =
    Boolean(onEdit) && canOfferAct(session, 'tpabilling.enrollment.edit');

  return (
    <DataTable<Client>
      columns={clientColumns}
      data={query.data?.items ?? []}
      getRowId={(row) => row.id}
      tableId="clients"
      actions={
        showEdit
          ? (client) => (
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Edit ${client.name}`}
                onClick={() => onEdit?.(client)}
              >
                <Pencil className="size-4" />
              </Button>
            )
          : undefined
      }
      isLoading={query.isLoading}
      isError={query.isError}
      errorMessage="Failed to load clients."
      emptyTitle="No clients found"
      page={query.data?.page ?? params.page ?? 1}
      pageSize={query.data?.pageSize ?? params.pageSize ?? 25}
      total={query.data?.total ?? 0}
      onPageChange={onPageChange}
      onRowClick={onRowClick}
    />
  );
}
